import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';

import {
  CELSIUS_ID, FAHRENHEIT_ID, SPEED_FEET, SPEED_METERS,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import dashboardApi, { getPublicDashboardToken } from '../../services/dashboardApi';
import AlertHistoryTableRow from './AlertHistoryTableRow';
import {
  ALERT_HISTORY_COLUMNS, buildColumnVisibility, COLUMN_ORDER, toDisabledColumns,
} from './alertHistoryColumns';
import PaginatedListNavigation from '../../../paginatedlist/PaginatedListNavigation/PaginatedListNavigation';
import { ALERT_HISTORY_DEFAULT_CONTEXT } from './alertHistoryDefaultContext';

const DEFAULT_ROWS_PER_PAGE = 10;

const LABEL_TO_ID = Object.entries(ALERT_HISTORY_COLUMNS).reduce((acc, [id, cfg]) => {
  acc[cfg.label] = id;
  return acc;
}, {});

const fetchAlertHistoryPage = (widgetId, pageNo, pageSize) => dashboardApi.alertHistory
  .getHistory(widgetId, pageNo, pageSize)
  .then((response) => {
    const body = dashboardApi.unwrapBody(response) || {};
    const pageInfo = body.pageInfo || {};
    return {
      events: body.alertEventList || [],
      totalResults: typeof pageInfo.totalRecords === 'number' ? pageInfo.totalRecords : 0,
      hasNextPage: !!pageInfo.hasNext,
    };
  })
  .catch(() => ({ events: [], totalResults: 0, hasNextPage: false }));

// Parent-driven hook. `currentPage` is owned here as *local* state and never
// leaks into context — same contract as useAlertsListWidgetData.
export const useAlertHistoryWidgetData = (context = {}, options = {}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [pageNo, setPageNo] = useState(0);
  const { isEditMode = false, widgetId = null, refreshTick = 0 } = options;

  const { rowsPerPage } = { ...ALERT_HISTORY_DEFAULT_CONTEXT, ...context };

  // Reset to page 0 whenever the server-side query changes.
  useEffect(() => {
    setPageNo(0);
  }, [rowsPerPage, widgetId]);

  useEffect(() => {
    if (isEditMode) return undefined;
    let cancelled = false;
    setIsLoading(true);

    if (widgetId === undefined || widgetId === null || widgetId === '') {
      setData({ events: [], totalResults: 0, hasNextPage: false });
      setIsLoading(false);
      return undefined;
    }

    fetchAlertHistoryPage(widgetId, pageNo, rowsPerPage).then((result) => {
      if (cancelled) return;
      setData(result);
      setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [widgetId, pageNo, rowsPerPage, isEditMode, refreshTick]);

  const viewState = useMemo(() => ({ pageNo, setPageNo }), [pageNo]);

  return { isLoading, data, viewState };
};

const AlertHistoryWidgetView = ({
  data, context, viewState, onContextChange, userPreferences,
}) => {
  const events = (data && data.events) || [];
  const totalResults = (data && data.totalResults) || 0;

  // currentPage is transient view state — sourced from the hook, never context.
  const { pageNo = 0, setPageNo = () => {} } = viewState;
  const { disabledColumns, rowsPerPage = DEFAULT_ROWS_PER_PAGE } = context;

  // On public pages, viewers can't mutate the owner's persisted context, so
  // the column-picker cog is hidden.
  const isPublicPage = !!getPublicDashboardToken();

  // Internal id→bool visibility, driven by disabledColumns.
  const columnVisibility = useMemo(
    () => buildColumnVisibility(disabledColumns),
    [disabledColumns],
  );

  // Label-keyed map for the cog menu.
  const columnVisibilityByLabel = useMemo(
    () => COLUMN_ORDER.reduce((acc, id) => {
      acc[ALERT_HISTORY_COLUMNS[id].label] = columnVisibility[id];
      return acc;
    }, {}),
    [columnVisibility],
  );

  const handlePrevPage = useCallback(() => {
    setPageNo(Math.max(0, pageNo - 1));
  }, [pageNo, setPageNo]);

  const handleNextPage = useCallback(() => {
    setPageNo(pageNo + 1);
  }, [pageNo, setPageNo]);

  // Persist rowsPerPage via context. currentPage reset happens in the hook.
  const handleSetRowsPerPage = useCallback((nextSize) => {
    const size = nextSize > 0 ? nextSize : DEFAULT_ROWS_PER_PAGE;
    onContextChange({ ...context, rowsPerPage: size });
  }, [context, onContextChange]);

  // Persist disabledColumns via context.
  const handleToggleColumn = useCallback((label, visible) => {
    const columnId = LABEL_TO_ID[label];
    if (!columnId) return;
    const nextVisibility = { ...columnVisibility, [columnId]: visible };
    onContextChange({
      ...context,
      disabledColumns: toDisabledColumns(nextVisibility),
    });
  }, [context, columnVisibility, onContextChange]);

  if (!events || events.length === 0) {
    return (
      <div className="h-100 w-100 d-flex align-items-center justify-content-center">
        <span className="alert alert-warning mb-0">No alerts have triggered yet.</span>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100 w-100">
      <div className="d-flex px-2 pt-1">
        <div className="ml-auto">
          <PaginatedListNavigation
            currentPage={pageNo}
            totalResults={totalResults}
            onPrevPage={handlePrevPage}
            onNextPage={handleNextPage}
            columnVisibility={columnVisibilityByLabel}
            onToggleColumn={isPublicPage ? () => {} : handleToggleColumn}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={isPublicPage ? () => {} : handleSetRowsPerPage}
            showSettings={!isPublicPage}
          />
        </div>
      </div>
      <div className="flex-grow-1" style={{ overflowY: 'auto' }}>
        <table className="table table-sm table-striped mb-0">
          <thead>
            <tr>
              {COLUMN_ORDER
                .filter((id) => columnVisibility[id])
                .map((id) => (
                  <th key={id}>{ALERT_HISTORY_COLUMNS[id].label}</th>
                ))}
            </tr>
          </thead>
          <tbody>
            {events.map((alert, index) => (
              <AlertHistoryTableRow
              // eslint-disable-next-line react/no-array-index-key
                key={`${alert.alertId || 'row'}-${index}`}
                alert={alert}
                columnVisibility={columnVisibility}
                userPreferences={userPreferences}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

AlertHistoryWidgetView.propTypes = {
  data: PropTypes.shape({
    events: PropTypes.arrayOf(PropTypes.object),
    totalResults: PropTypes.number,
    hasNextPage: PropTypes.bool,
  }),
  context: PropTypes.shape({
    rowsPerPage: PropTypes.number,
    disabledColumns: PropTypes.arrayOf(PropTypes.string),
  }),
  viewState: PropTypes.shape({
    pageNo: PropTypes.number,
    setPageNo: PropTypes.func,
  }),
  onContextChange: PropTypes.func,
  userPreferences: PropTypes.shape({
    temperatureScale: PropTypes.oneOf([CELSIUS_ID, FAHRENHEIT_ID]),
    airSpeedScale: PropTypes.oneOf([SPEED_METERS, SPEED_FEET]),
  }),
};

AlertHistoryWidgetView.defaultProps = {
  data: null,
  context: {},
  viewState: {},
  onContextChange: () => {},
  userPreferences: {
    temperatureScale: CELSIUS_ID,
    airSpeedScale: SPEED_METERS,
  },
};

export default AlertHistoryWidgetView;
